import {
  Component,
  inject,
  input,
  output,
  model,
  signal,
  computed,
  effect,
  contentChild,
  InjectionToken,
  Injector,
  TemplateRef,
  ChangeDetectionStrategy,
  ElementRef,
  viewChild,
  untracked,
  afterEveryRender,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import { ZagIcon } from '../icon/icon';
import { ZagButton } from '../button/button';
import { Badge } from '../badge/badge';
import { CollectionLayoutControlsComponent } from './collection-layout-controls.component';
import { CollectionSelectionService } from '../selection/collection-selection.service';
import { SelectableDirective } from '../selection/selectable.directive';
import { ToastService } from '../toast/toast.service';
import type { BulkEditSection } from '../bulk-edit/bulk-edit-section.types';
import { BulkEditDialogComponent, type BulkEditDialogData } from '../bulk-edit/bulk-edit-dialog.component';
import type { BulkSelectionFilter, BulkSelectionPayload } from '../bulk-edit/bulk-edit.models';
import { BulkEditService } from '../bulk-edit/bulk-edit.service';
import { RecentBulkEditsCardComponent } from '../bulk-edit/recent-bulk-edits-card.component';
import {
  CollectionLayoutConfig,
  CollectionFilterOption,
  CollectionFilterGroup,
  CollectionFilterValues,
  type CollectionFilterPrimitive,
  CollectionSortDirection,
  type CollectionStat,
} from './types';
import { UserScopedStorageService } from '../services/user-scoped-storage.service';
import type { CollectionSelectionState } from '../selection/collection-selection.service';
import { SearchService } from '../services/search.service';
import { FIELD_PERMISSION_CHECKER, type FieldPermissionChecker } from '../entity-edit/field-permission-checker';
import { FEATURE_CHECKER, type FeatureChecker } from '../entity-edit/feature-checker';

const LEGACY_FILTER_GROUP_ID = 'default';
const BULK_EDIT_EXECUTE_PERMISSION = 'bulk-edit:execute';
const BULK_EDIT_EXECUTE_FEATURE = 'bulk-edit.execute';

/**
 * A reusable layout component for displaying collections of items
 * with view mode switching, sorting, search, and infinite scroll.
 *
 * Uses content projection for maximum flexibility in card/list templates.
 *
 * @example
 * ```html
 * <zag-collection-layout
 *   [items]="items()"
 *   [trackBy]="trackById"
 *   [config]="config"
 *   [loading]="isLoading()"
 *   [(searchTerm)]="searchTerm">
 *
 *   <ng-template #headerContent>
 *     <h1>My Items</h1>
 *     <button zagButton (click)="onCreate()">Create</button>
 *   </ng-template>
 *
 *   <ng-template #cardTemplate let-item>
 *     <my-item-card [item]="item"></my-item-card>
 *   </ng-template>
 *
 *   <ng-template #listTemplate let-item>
 *     <my-item-row [item]="item"></my-item-row>
 *   </ng-template>
 * </zag-collection-layout>
 * ```
 */
@Component({
  selector: 'zag-collection-layout',
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    ZagIcon,
    ZagButton,
    Badge,
    CollectionLayoutControlsComponent,
    SelectableDirective,
    RecentBulkEditsCardComponent,
  ],
  templateUrl: './collection-layout.component.html',
  styleUrls: ['./collection-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CollectionSelectionService],
})
export class CollectionLayoutComponent<T> {
  // ─────────────────────────────────────────────────────────────────────────────
  // Inputs
  // ─────────────────────────────────────────────────────────────────────────────

  /** Array of items to display */
  items = input.required<T[]>();

  /** Track by function for ngFor optimization */
  trackBy = input.required<(item: T) => string | number>();

  /** Configuration for view modes, sorting, search, etc. */
  config = input<CollectionLayoutConfig<T>>({});

  /** Loading state */
  loading = input<boolean>(false);

  /** Whether more API pages are available beyond what is currently rendered. */
  hasMorePages = input<boolean>(false);

  /** Whether a next-page API fetch is currently in flight. */
  loadingMorePages = input<boolean>(false);

  /** Error state (any truthy value indicates an error) */
  error = input<unknown>(null);

  /** Whether the projected header actions card should be rendered. */
  showHeaderActions = input<boolean>(true);

  /** Where toolbarContent should render: own card or merged into profile card. */
  toolbarContentPlacement = input<'card' | 'profile'>('card');

  /**
   * Total number of items matching the current filter/search (from API meta.total).
   * When provided, shown as the primary stat in the stats card.
   */
  totalCount = input<number | undefined>(undefined);

  /**
   * Grand total of all items with no filters applied.
   * When provided alongside totalCount, both are shown in the stats card:
   * totalCount labeled "FILTERED" and unfilteredTotal labeled "TOTAL".
   */
  unfilteredTotal = input<number | undefined>(undefined);

  /** Total selectable rows matching current query when it differs from totalCount. */
  selectionTotal = input<number | undefined>(undefined);

  /** Domain KPIs appended to the stats card after Total and Filtered. */
  stats = input<CollectionStat[]>([]);

  /**
   * Dynamic filter groups — overrides config.filterGroups when provided.
   */
  filterGroups = input<CollectionFilterGroup[] | undefined>(undefined);

  /**
   * Dynamic filter options — overrides config.filterOptions when provided.
   * Use this when filter options are loaded asynchronously (e.g. from the API).
   */
  filterOptions = input<CollectionFilterOption<CollectionFilterPrimitive>[] | undefined>(undefined);

  /** Opt into bulk-selection mode. Defaults to false so existing consumers are unaffected. */
  selectionEnabled = input<boolean>(false);

  /** Token used to resolve BulkEditSection providers from the DI tree. */
  bulkEditToken = input<InjectionToken<BulkEditSection[]> | undefined>(undefined);

  /**
   * API resource path (e.g. `'persons'`). When set alongside `bulkEditToken`,
   * the collection-layout opens the bulk-edit dialog directly instead of
   * delegating via `openBulkEdit` output.
   */
  resourcePath = input<string | undefined>(undefined);

  /** Predicate used to keep non-updatable rows out of selection mode. */
  selectable = input<(item: T) => boolean>(() => true);

  /** ID sent to bulk-update endpoints for a selected row. */
  selectionId = input<((item: T) => string | number) | undefined>(undefined);

  // ─────────────────────────────────────────────────────────────────────────────
  // Two-way bindable state (models)
  // ─────────────────────────────────────────────────────────────────────────────

  /** Current view mode ID */
  viewMode = model<string>('card');

  /** Current sort option ID */
  sortBy = model<string | undefined>(undefined);

  /** Current sort direction */
  sortDirection = model<CollectionSortDirection>('asc');

  /**
   * Currently selected filter value — used when `config().filterOptions` is provided.
   * Two-way bindable so the host component can react to filter changes.
   */
  filterValue = model<CollectionFilterPrimitive | undefined>(undefined);

  /** Current grouped filter values */
  filterValues = model<CollectionFilterValues>({});

  /** Whether selection mode is active (two-way bindable). */
  selectionMode = model<boolean>(false);

  /**
   * Initial selection state — applied once on first change detection. Useful
   * for restoring selection from URL state or seeding mockups/tests.
   *
   * - `mode: 'allMatching'` toggles the service into allMatching mode using
   *   the current totalCount + filter snapshot.
   * - `ids`: explicit ids to select. Ignored when `mode` is `'allMatching'`.
   */
  initialSelection = input<{ ids?: readonly string[]; excludeIds?: readonly string[]; mode?: 'explicit' | 'allMatching' } | undefined>(
    undefined
  );

  // Search is now handled by global TopBar search via SearchService
  // The searchTerm model has been removed from this component

  // ─────────────────────────────────────────────────────────────────────────────
  // Outputs
  // ─────────────────────────────────────────────────────────────────────────────

  /** Emitted when retry button is clicked */
  retry = output<void>();

  /** Emitted when "Bulk edit…" is triggered — carries the resolved bulkEditToken. */
  openBulkEdit = output<InjectionToken<BulkEditSection[]> | undefined>();

  /** Emitted when the user scrolls near the bottom and no local items remain — parent should call service.loadNextPage(). */
  loadMorePages = output<void>();

  // ─────────────────────────────────────────────────────────────────────────────
  // Content projection (signal-based)
  // ─────────────────────────────────────────────────────────────────────────────

  /** Template for header content (title, actions) */
  headerContent = contentChild<TemplateRef<void>>('headerContent');

  /** Template for card view items */
  cardTemplate = contentChild<TemplateRef<{ $implicit: T }>>('cardTemplate');

  /** Template for list view items */
  listTemplate = contentChild<TemplateRef<{ $implicit: T }>>('listTemplate');

  /** Template for empty state */
  emptyContent = contentChild<TemplateRef<void>>('emptyContent');

  /** Template for additional toolbar content (e.g., filters, dropdowns) */
  toolbarContent = contentChild<TemplateRef<void>>('toolbarContent');

  /** Template for actions owned by the active selection card. */
  selectionActions = contentChild<TemplateRef<unknown>>('selectionActions');

  // ─────────────────────────────────────────────────────────────────────────────
  // View children
  // ─────────────────────────────────────────────────────────────────────────────

  /** Reference to the scrollable content container */
  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  // ─────────────────────────────────────────────────────────────────────────────
  // Injected services
  // ─────────────────────────────────────────────────────────────────────────────

  protected readonly selectionService = inject(CollectionSelectionService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly route = inject(ActivatedRoute, { optional: true });
  private readonly router = inject(Router, { optional: true });
  private readonly dialog = inject(Dialog);
  private readonly injector = inject(Injector);
  private readonly userStorage = inject(UserScopedStorageService);
  private readonly searchService = inject(SearchService);
  private readonly bulkEditService = inject(BulkEditService, { optional: true });
  private readonly fieldPermissions = inject<FieldPermissionChecker | null>(FIELD_PERMISSION_CHECKER, { optional: true });
  private readonly featureChecker = inject<FeatureChecker | null>(FEATURE_CHECKER, { optional: true });

  protected readonly canBulkEdit = computed(() => this.fieldPermissions?.hasPermission(BULK_EDIT_EXECUTE_PERMISSION) ?? false);

  protected readonly bulkEditAvailable = computed(
    () => this.canBulkEdit() && (this.featureChecker?.hasFeature(BULK_EDIT_EXECUTE_FEATURE) ?? true)
  );

  protected readonly selectionAvailable = computed(
    () => this.selectionEnabled() && (!!this.selectionActions() || this.bulkEditAvailable())
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Internal state
  // ─────────────────────────────────────────────────────────────────────────────

  /** jobId read from ?bulkJob query param — forwarded to bulk-edit dialog opener. */
  protected readonly bulkJobId = signal<string | null>(null);

  /** Tracks the last jobId that was auto-opened to avoid double-open while allowing switching jobs. */
  private lastAutoOpenedJobId: string | null = null;

  /** Number of items currently loaded for infinite scroll */
  private readonly loadedCount = signal(20);

  /** Invalidates pending select-all responses when selection intent changes. */
  private selectAllRequestId = 0;

  /** Whether more items are currently being loaded */
  protected readonly loadingMore = signal(false);

  /**
   * Tracks whether the content has been displayed at least once.
   * Once true, subsequent reloads keep the content visible (no full-page spinner).
   */
  private readonly hasEverLoaded = signal(false);

  /** Storage key used for the last state hydration. */
  private lastInitializedStorageKey: string | null = null;

  /** Whether initial state hydration has already run for this component instance. */
  private hasInitializedState = false;

  // ─────────────────────────────────────────────────────────────────────────────
  // Computed values
  // ─────────────────────────────────────────────────────────────────────────────

  /** Whether current view mode is a card mode (grid layout) */
  protected readonly isCardMode = computed(() => {
    const modes = this.config().viewModes;
    if (!modes || modes.length === 0) return true;

    const currentMode = modes.find(m => m.id === this.viewMode());
    return currentMode?.isCardMode ?? true;
  });

  /** Items after sorting */
  protected readonly sortedItems = computed(() => {
    const items = this.items();
    const sortId = this.sortBy();
    const direction = this.sortDirection();
    const options = this.config().sortOptions;

    if (!sortId || !options) return items;

    const sortOption = options.find(o => o.id === sortId);
    if (!sortOption?.compareFn) return items;

    const sorted = [...items].sort(sortOption.compareFn);
    return direction === 'desc' ? sorted.reverse() : sorted;
  });

  /** Items visible (sliced for infinite scroll) */
  protected readonly visibleItems = computed(() => {
    const items = this.sortedItems();
    const count = this.loadedCount();
    return items.slice(0, count);
  });

  /** Whether there are more items to show — either local items not yet rendered or more API pages available */
  protected readonly hasMore = computed(() => this.loadedCount() < this.sortedItems().length || this.hasMorePages());

  /** Whether the profile card should be shown (title or icon configured) */
  protected readonly hasProfile = computed(() => {
    const cfg = this.config();
    return !!(cfg.title || cfg.icon);
  });

  /**
   * True only during the very first load (no prior data).
   * Subsequent reloads (e.g. filter changes) are NOT treated as initial loads.
   */
  protected readonly isInitialLoad = computed(() => this.loading() && !this.hasEverLoaded());

  /** Whether the stats card should be shown */
  protected readonly showStats = computed(() => {
    return this.totalCount() !== undefined || !this.isInitialLoad();
  });

  /** Resolved filter options: dynamic input overrides config.filterOptions */
  protected readonly resolvedFilterOptions = computed<CollectionFilterOption<CollectionFilterPrimitive>[] | undefined>(
    () => this.filterOptions() ?? this.config().filterOptions
  );

  protected readonly resolvedFilterGroups = computed<CollectionFilterGroup[] | undefined>(() => {
    const explicitGroups = this.filterGroups() ?? this.config().filterGroups;
    if ((explicitGroups?.length ?? 0) > 0) {
      return explicitGroups;
    }

    const options = this.resolvedFilterOptions();
    if ((options?.length ?? 0) === 0) {
      return undefined;
    }

    return [
      {
        id: LEGACY_FILTER_GROUP_ID,
        label: this.config().filterLabel ?? 'common.filter',
        options: options as CollectionFilterOption<CollectionFilterPrimitive>[],
        allowMultiple: false,
      },
    ];
  });

  protected readonly resolvedFilterValues = computed<CollectionFilterValues>(() => {
    if (this.usesExplicitFilterGroups()) {
      return this.filterValues();
    }

    const legacyValue = this.filterValue();
    if (legacyValue === undefined || legacyValue === null) {
      return {};
    }

    return { [LEGACY_FILTER_GROUP_ID]: [legacyValue as CollectionFilterPrimitive] };
  });

  /** Whether to show controls (view modes, sort options, or filter options available) */
  protected readonly hasControls = computed(() => {
    const cfg = this.config();
    return (cfg.viewModes?.length ?? 0) > 0 || (cfg.sortOptions?.length ?? 0) > 0 || (this.resolvedFilterGroups()?.length ?? 0) > 0;
  });

  protected readonly showToolbarInProfile = computed(() => {
    return this.hasProfile() && this.toolbarContentPlacement() === 'profile' && !!this.toolbarContent();
  });

  protected readonly showToolbarCard = computed(() => {
    return !!this.toolbarContent() && !this.showToolbarInProfile();
  });

  /** Whether to show empty state */
  protected readonly showEmpty = computed(
    () => !this.isInitialLoad() && !this.loading() && !this.error() && this.sortedItems().length === 0
  );

  /** Collection key derived from storageKeyPrefix or resourcePath. */
  protected readonly collectionKey = computed(() => this.config().storageKeyPrefix ?? this.resourcePath() ?? '');

  /** Count of currently selected items (explicit or allMatching total). */
  protected readonly selectionCount = computed(() => this.selectionService.count());

  /** Total rows available to the current collection query. */
  protected readonly matchingSelectionTotal = computed(() => this.selectionTotal() ?? this.totalCount() ?? this.sortedItems().length);

  /** Public selection snapshot for custom mutation consumers. */
  readonly selection = computed<CollectionSelectionState>(() => ({
    ...this.selectionService.state(),
    total: this.selectionService.mode() === 'allMatching' ? this.selectionService.total() : this.matchingSelectionTotal(),
  }));

  /** Backend-shaped payload matching the current selection state. */
  private readonly selectionPayload = computed<BulkSelectionPayload>(() => {
    if (this.selectionService.mode() === 'allMatching') {
      const filter = this.selectionService.filterSnapshot() as BulkSelectionFilter;
      const excludedIds = this.selectionService.excludedIds();
      return excludedIds.size > 0 ? { filter, excludeIds: [...excludedIds] } : { filter };
    }
    return { ids: [...this.selectionService.selectedIds()] };
  });

  /**
   * Template context for the `selectionActions` slot. Exposes ergonomic keys
   * (selectedCount / matchingTotal / allMatching / selectionPayload) that
   * projected panels bind to directly — the raw {@link CollectionSelectionState}
   * is available via the `state` key for advanced consumers.
   */
  protected readonly selectionContext = computed(() => {
    const state = this.selection();
    return {
      state,
      selectedCount: this.selectionCount(),
      matchingTotal: this.matchingSelectionTotal(),
      allMatching: state.mode === 'allMatching',
      selectionPayload: this.selectionPayload(),
    };
  });

  private readonly currentSelectionFilterSnapshot = computed(() => ({
    query: {
      search: this.searchService.catalogSearchQuery(),
      filter: this.toFilterExpressions(this.resolvedFilterValues()),
    },
    filterValues: this.filterValues(),
    filterValue: this.filterValue(),
    sortBy: this.sortBy(),
    sortDirection: this.sortDirection(),
  }));

  /** Whether "select all matching" is active. */
  protected readonly selectionAllMatchingActive = computed(() => this.selectionService.mode() === 'allMatching');

  // ─────────────────────────────────────────────────────────────────────────────
  // Constructor & Lifecycle
  // ─────────────────────────────────────────────────────────────────────────────

  constructor() {
    // React to ?bulkJob query param changes (including after initial render)
    this.route?.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
      this.bulkJobId.set(params.get('bulkJob'));
    });

    // Show a toast when allMatching selection is cleared due to filter change
    this.selectionService.cleared$.pipe(takeUntilDestroyed()).subscribe(() => {
      const msg = this.translateService.instant('common.selectionCleared');
      this.toastService.warning(msg);
      untracked(() => this.selectionMode.set(false));
    });

    // Apply initialSelection once when both the input and the item list are settled
    let initialSelectionApplied = false;
    effect(() => {
      const initial = this.initialSelection();
      const items = this.items();
      if (!initial || initialSelectionApplied || items.length === 0) return;
      initialSelectionApplied = true;
      untracked(() => {
        if (initial.mode === 'allMatching') {
          const total = this.matchingSelectionTotal();
          this.selectionService.selectAllMatching(total, this.currentSelectionFilterSnapshot());
          for (const id of initial.excludeIds ?? []) {
            this.selectionService.toggle(id);
          }
          return;
        }
        for (const id of initial.ids ?? []) {
          this.selectionService.toggle(id);
        }
      });
    });

    // Mark as ever-loaded once the first load completes (loading → not loading)
    effect(() => {
      if (!this.loading() && !this.hasEverLoaded()) {
        untracked(() => this.hasEverLoaded.set(true));
      }
    });

    effect(() => {
      const filterSnapshot = this.currentSelectionFilterSnapshot();
      untracked(() => this.selectionService.snapshotFilter(filterSnapshot));
    });

    // Reset loaded count when a fetch completes with a new result set.
    // We track loading() transitioning false→true (a reload started) and
    // then reset loadedCount once loading() returns to false (fetch done).
    // This prevents the intermediate empty-items state during a filter change
    // from resetting the count before new data arrives, which would cause items
    // to visually vanish and re-render (flicker).
    effect(() => {
      const isLoading = this.loading();
      const initial = this.config().initialBatchSize ?? 20;
      if (!isLoading) {
        untracked(() => this.loadedCount.set(initial));
      }
    });

    // Initialize view mode and sort from config defaults or localStorage
    effect(() => {
      const cfg = this.config();
      untracked(() => {
        const storageKey = cfg.storageKeyPrefix ?? null;
        const shouldHydrateFromStorage = !this.hasInitializedState || storageKey !== this.lastInitializedStorageKey;

        if (shouldHydrateFromStorage) {
          this.initializeState(cfg);
          this.lastInitializedStorageKey = storageKey;
          this.hasInitializedState = true;
          return;
        }

        this.syncStateWithAvailableOptions(cfg);
      });
    });

    // Fill the container on every render pass so card-mode grids on wide screens
    // always load enough rows to overflow — otherwise the scroll event never fires
    // and the user gets stuck on the initial batch.
    afterEveryRender(() => this.fillContainerIfNeeded());

    // Auto-open bulk-edit dialog when ?bulkJob is present and resourcePath is configured
    effect(() => {
      const jobId = this.bulkJobId();
      const rp = this.resourcePath();
      if (jobId && rp && this.lastAutoOpenedJobId !== jobId) {
        untracked(() => {
          this.lastAutoOpenedJobId = jobId;
          this.openBulkEditDialog(jobId);
        });
      }
    });

    // Persist state to localStorage when it changes
    effect(() => {
      const viewModeValue = this.viewMode();
      const sortByValue = this.sortBy();
      const sortDirectionValue = this.sortDirection();
      const filterValues = this.resolvedFilterValues();
      const cfg = untracked(() => this.config());

      if (cfg.storageKeyPrefix) {
        this.saveToStorage(cfg.storageKeyPrefix, viewModeValue, sortByValue, sortDirectionValue, filterValues);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Event handlers
  // ─────────────────────────────────────────────────────────────────────────────

  /** Handle scroll for infinite scroll */
  protected onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const threshold = this.config().scrollThreshold ?? 200;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    if (nearBottom && this.hasMore() && !this.loadingMore() && !this.loadingMorePages()) {
      this.tryLoadMore();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Private methods
  // ─────────────────────────────────────────────────────────────────────────────

  /** Render the next local batch; if local items are exhausted, request the next API page. */
  private tryLoadMore(): void {
    if (this.loadedCount() < this.sortedItems().length) {
      this.loadMoreLocal();
    } else if (this.hasMorePages() && !this.loadingMorePages()) {
      this.loadMorePages.emit();
    }
  }

  /** Advance the local render window by one batch. */
  private loadMoreLocal(): void {
    const batchSize = this.config().loadMoreBatchSize ?? 10;
    this.loadedCount.update(count => Math.min(count + batchSize, this.sortedItems().length));
  }

  /**
   * Proactively load more items when the content doesn't overflow the container.
   * In card/grid mode on wide screens the initial batch may fit entirely in the
   * visible area, which means the scroll event never fires and infinite scroll
   * stalls. Called after every render pass via afterRender().
   */
  private fillContainerIfNeeded(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container || !this.hasMore() || this.loading() || this.loadingMorePages()) return;

    // clientHeight is 0 in JSDOM (no layout engine) — skip to avoid loading
    // all items in test environments where scroll dimensions are meaningless.
    if (container.clientHeight === 0) return;

    const threshold = this.config().scrollThreshold ?? 200;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    if (nearBottom) {
      this.tryLoadMore();
    }
  }

  protected handleFilterValuesChange(nextFilterValues: CollectionFilterValues): void {
    if (this.usesExplicitFilterGroups()) {
      this.filterValues.set(nextFilterValues);
      return;
    }

    const legacySelection = nextFilterValues[LEGACY_FILTER_GROUP_ID] ?? [];
    this.filterValue.set((legacySelection[0] as CollectionFilterPrimitive | undefined) ?? this.getLegacyDefaultFilterValue());
  }

  private usesExplicitFilterGroups(): boolean {
    return (this.filterGroups()?.length ?? 0) > 0 || (this.config().filterGroups?.length ?? 0) > 0;
  }

  /** Initialize state from config defaults or localStorage */
  private initializeState(cfg: CollectionLayoutConfig<T>): void {
    const prefix = cfg.storageKeyPrefix;
    const availableViewModeIds = new Set((cfg.viewModes ?? []).map(mode => mode.id));
    const availableSortIds = new Set((cfg.sortOptions ?? []).map(option => option.id));

    // Load from storage or use defaults
    const storedViewMode = prefix ? this.getFromStorage(`${prefix}-view-mode`) : null;
    const storedSortBy = prefix ? this.getFromStorage(`${prefix}-sort-by`) : null;
    const storedSortDirection = prefix ? this.getFromStorage(`${prefix}-sort-direction`) : null;
    const storedFilterValues = prefix ? this.getFilterValuesFromStorage(`${prefix}-filter-values`) : null;

    // Set view mode
    this.viewMode.set(this.resolveViewMode(cfg, this.viewMode(), storedViewMode, availableViewModeIds));

    // Set sort
    this.sortBy.set(this.resolveSortBy(cfg, this.sortBy(), storedSortBy, availableSortIds));

    // Set sort direction
    const defaultSortDirection = cfg.defaultSortDirection ?? 'asc';
    const resolvedDirection = storedSortDirection === 'asc' || storedSortDirection === 'desc' ? storedSortDirection : defaultSortDirection;
    this.sortDirection.set(resolvedDirection);

    if (storedFilterValues) {
      this.applyStoredFilterState(storedFilterValues);
      return;
    }

    this.syncStateWithAvailableOptions(cfg);
  }

  private syncStateWithAvailableOptions(cfg: CollectionLayoutConfig<T>): void {
    const availableViewModeIds = new Set((cfg.viewModes ?? []).map(mode => mode.id));
    const currentViewMode = this.viewMode();
    if (currentViewMode && availableViewModeIds.size > 0 && !availableViewModeIds.has(currentViewMode)) {
      this.viewMode.set(cfg.defaultViewMode ?? cfg.viewModes?.[0]?.id ?? 'card');
    }

    const availableSortIds = new Set((cfg.sortOptions ?? []).map(option => option.id));
    const currentSortBy = this.sortBy();
    if (currentSortBy && !availableSortIds.has(currentSortBy)) {
      const fallbackSortBy = cfg.defaultSortBy ?? cfg.sortOptions?.[0]?.id;
      this.sortBy.set(fallbackSortBy && availableSortIds.has(fallbackSortBy) ? fallbackSortBy : undefined);
    }

    if (this.usesExplicitFilterGroups()) {
      const sanitizedFilterValues = this.sanitizeFilterValues(this.filterValues());
      if (!this.areFilterValuesEqual(this.filterValues(), sanitizedFilterValues)) {
        this.filterValues.set(sanitizedFilterValues);
      }
      return;
    }

    const currentFilterValue = this.filterValue();
    if (currentFilterValue === undefined || currentFilterValue === null) {
      return;
    }

    if (this.getValidLegacyFilterValue(currentFilterValue) === undefined) {
      this.filterValue.set(this.getLegacyDefaultFilterValue());
    }
  }

  private resolveViewMode(
    cfg: CollectionLayoutConfig<T>,
    currentViewMode: string,
    storedViewMode: string | null,
    availableViewModeIds: Set<string>
  ): string {
    if (currentViewMode && currentViewMode !== 'card' && availableViewModeIds.has(currentViewMode)) {
      return currentViewMode;
    }

    if (storedViewMode && availableViewModeIds.has(storedViewMode)) {
      return storedViewMode;
    }

    const defaultViewMode = cfg.defaultViewMode ?? cfg.viewModes?.[0]?.id ?? 'card';
    if (availableViewModeIds.has(defaultViewMode)) {
      return defaultViewMode;
    }

    return cfg.viewModes?.[0]?.id ?? 'card';
  }

  private resolveSortBy(
    cfg: CollectionLayoutConfig<T>,
    currentSortBy: string | undefined,
    storedSortBy: string | null,
    availableSortIds: Set<string>
  ): string | undefined {
    if (currentSortBy && availableSortIds.has(currentSortBy)) {
      return currentSortBy;
    }

    if (storedSortBy && availableSortIds.has(storedSortBy)) {
      return storedSortBy;
    }

    const defaultSortBy = cfg.defaultSortBy ?? cfg.sortOptions?.[0]?.id;
    return defaultSortBy && availableSortIds.has(defaultSortBy) ? defaultSortBy : undefined;
  }

  private applyStoredFilterState(storedFilterValues: CollectionFilterValues): void {
    if (this.usesExplicitFilterGroups()) {
      this.filterValues.set(this.sanitizeFilterValues(storedFilterValues));
      return;
    }

    const storedLegacyValue = (storedFilterValues[LEGACY_FILTER_GROUP_ID]?.[0] as CollectionFilterPrimitive | undefined) ?? null;
    this.filterValue.set(this.getValidLegacyFilterValue(storedLegacyValue) ?? this.getLegacyDefaultFilterValue());
  }

  private getLegacyDefaultFilterValue(): CollectionFilterPrimitive | undefined {
    const legacyOptions = this.resolvedFilterOptions();
    return legacyOptions?.[0]?.value as CollectionFilterPrimitive | undefined;
  }

  private getValidLegacyFilterValue(value: CollectionFilterPrimitive | null): CollectionFilterPrimitive | undefined {
    if (value === null) {
      return undefined;
    }

    const legacyOptions = this.resolvedFilterOptions();
    return legacyOptions?.some(option => option.value === value) ? value : undefined;
  }

  /** Save state to localStorage */
  private saveToStorage(
    prefix: string,
    viewModeValue: string,
    sortByValue: string | undefined,
    sortDirectionValue: CollectionSortDirection,
    filterValues: CollectionFilterValues
  ): void {
    this.userStorage.set(`${prefix}-view-mode`, viewModeValue);
    if (sortByValue) {
      this.userStorage.set(`${prefix}-sort-by`, sortByValue);
    }
    this.userStorage.set(`${prefix}-sort-direction`, sortDirectionValue);
    this.userStorage.set(`${prefix}-filter-values`, JSON.stringify(filterValues));
  }

  private getFromStorage(key: string): string | null {
    return this.userStorage.get(key);
  }

  private getFilterValuesFromStorage(key: string): CollectionFilterValues | null {
    const storedValue = this.getFromStorage(key);
    if (!storedValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(storedValue) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return null;
      }

      const entries = Object.entries(parsed).filter(([, value]) => Array.isArray(value));
      return Object.fromEntries(entries.map(([groupId, value]) => [groupId, value as CollectionFilterPrimitive[]]));
    } catch {
      return null;
    }
  }

  private sanitizeFilterValues(filterValues: CollectionFilterValues): CollectionFilterValues {
    const groups = this.resolvedFilterGroups() ?? [];
    const validOptionsByGroupId = new Map(
      groups.map(group => [group.id, new Set(group.options.map(option => option.value as CollectionFilterPrimitive))])
    );

    return Object.fromEntries(
      Object.entries(filterValues)
        .map(([groupId, values]) => {
          const validOptions = validOptionsByGroupId.get(groupId);
          if (!validOptions) {
            return null;
          }

          const nextValues = values.filter(value => validOptions.has(value));
          return [groupId, nextValues] as const;
        })
        .filter((entry): entry is readonly [string, CollectionFilterPrimitive[]] => entry !== null)
    );
  }

  protected enterSelectionMode(): void {
    if (!this.selectionAvailable()) return;
    this.selectionMode.set(true);
    this.selectionService.clear();
  }

  exitSelectionMode(): void {
    this.selectionMode.set(false);
    this.selectionService.clear();
  }

  onSelectAllMatching(active: boolean): void {
    if (!active) {
      this.selectionService.clear();
      return;
    }

    const filterSnapshot = this.currentSelectionFilterSnapshot();
    const resourcePath = this.resourcePath();
    if (!resourcePath || !this.bulkEditService) {
      this.selectionService.selectAllMatching(this.matchingSelectionTotal(), filterSnapshot);
      return;
    }

    this.bulkEditService.selectIds(resourcePath, filterSnapshot.query).subscribe({
      next: ({ total }) => this.selectionService.selectAllMatching(total, filterSnapshot),
      error: () => this.toastService.error(this.translateService.instant('common.error')),
    });
  }

  protected onBulkEditClick(): void {
    if (!this.bulkEditAvailable()) return;
    if (this.bulkEditToken() && this.resourcePath()) {
      this.openBulkEditDialog();
      return;
    }
    this.openBulkEdit.emit(this.bulkEditToken());
  }

  private openBulkEditDialog(bulkJobId?: string): void {
    if (!this.bulkEditAvailable()) return;
    const token = this.bulkEditToken();
    const rp = this.resourcePath();
    if (!token || !rp) return;

    const sections = this.injector.get(token, [], { optional: true }) ?? [];
    const selectionPayload = this.selectionPayload();

    const data: BulkEditDialogData = {
      sections,
      resourcePath: rp,
      collectionKey: this.collectionKey(),
      selectedCount: this.selectionService.count(),
      selectionPayload,
      bulkJobId,
      ...(bulkJobId
        ? {}
        : {
            entityNameResolver: this.buildEntityNameResolver(),
            entityResolver: (id: string) => this.items().find(i => this.getEntityId(i) === id),
            resultRowTemplate: this.listTemplate() as TemplateRef<{ $implicit: unknown }> | undefined,
          }),
    };

    const ref = this.dialog.open(BulkEditDialogComponent, {
      data,
      injector: this.injector,
      panelClass: ['zag-dialog-panel', 'zag-dialog-panel--sm'],
      backdropClass: 'zag-dialog-backdrop',
    });

    ref.closed.subscribe(() => {
      this.lastAutoOpenedJobId = null;
      this.bulkJobId.set(null);
      if (this.router && this.route?.snapshot.queryParamMap.has('bulkJob')) {
        void this.router.navigate([], { queryParams: { bulkJob: null }, queryParamsHandling: 'merge', replaceUrl: true });
      }
    });
  }

  protected getEntityId(item: T): string {
    return String((this.selectionId() ?? this.trackBy())(item));
  }

  private buildEntityNameResolver(): (id: string) => string | undefined {
    return (id: string) => {
      const item = this.items().find(i => this.getEntityId(i) === id);
      if (!item) return undefined;
      const anyItem = item as Record<string, unknown>;
      if (typeof anyItem['firstName'] === 'string' && typeof anyItem['lastName'] === 'string') {
        return `${anyItem['firstName']} ${anyItem['lastName']}`;
      }
      if (typeof anyItem['name'] === 'string') return anyItem['name'];
      if (typeof anyItem['label'] === 'string') return anyItem['label'];
      return undefined;
    };
  }

  protected isSelectable(item: T): boolean {
    return this.selectable()(item);
  }

  private toFilterExpressions(filterValues: CollectionFilterValues): string[] {
    const groups = new Map((this.resolvedFilterGroups() ?? []).map(group => [group.id, group]));

    return Object.entries(filterValues).flatMap(([groupId, values]) => {
      if (values.length === 0) return [];

      const group = groups.get(groupId);
      const field = group?.filterField ?? groupId;
      const operator = group?.filterOperator ?? (group?.allowMultiple || values.length > 1 ? 'in' : 'eq');
      return [`${field}:${operator}:${values.map(String).join(',')}`];
    });
  }

  private areFilterValuesEqual(left: CollectionFilterValues, right: CollectionFilterValues): boolean {
    const leftEntries = Object.entries(left);
    const rightEntries = Object.entries(right);
    if (leftEntries.length !== rightEntries.length) {
      return false;
    }

    return leftEntries.every(([groupId, leftValues]) => {
      const rightValues = right[groupId];
      return (
        Array.isArray(rightValues) &&
        leftValues.length === rightValues.length &&
        leftValues.every((value, index) => value === rightValues[index])
      );
    });
  }
}
